/* eslint-disable no-param-reassign */
import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueInfoSnackbar, enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { notifyReindexRequired } from '../features/operation-supervisor/indexingRequestEvents';

const initialState = {
  entities: {}, // { [id]: { id, title, weight, autocomplete } }
  ids: [],      // ordered list of field IDs
  savedSnapshot: {}, // copy of entities at last load/save — used by discard
  modifiedIds: [],   // ids of fields with local unsaved changes
  isFetching: false,
  isSaving: false,
  error: null,      // last error (load or save) — kept for backward compat
  loadError: null,  // only set by load failure; cleared on next load attempt
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const loadFields = createAsyncThunk(
  'fields/loadFieldsAsync',
  async (shopId, { extra, dispatch, rejectWithValue }) => {
    try {
      const { fieldsApi } = extra.serviceRegistry;
      const result = await fieldsApi.load(shopId);
      return result.fields;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(getLocalizedMessage('fields.loadError', {})));
      return rejectWithValue(err?.message || err?.errorCode || err?.error || 'load failed');
    }
  },
);

export const saveFields = createAsyncThunk(
  'fields/saveFieldsAsync',
  async (request, { extra, getState, dispatch, rejectWithValue }) => {
    try {
      const shopId = request && typeof request === 'object' ? request.shopId : request;
      const deferredReindex = request && typeof request === 'object'
        ? request.deferredReindex === true
        : false;
      const { fieldsApi, indexingApi } = extra.serviceRegistry;
      const allFields = selectAllFields(getState());
      const result = await fieldsApi.save(shopId, allFields);
      dispatch(enqueueInfoSnackbar(getLocalizedMessage('fields.saveSuccess', {})));
      if (deferredReindex) {
        notifyReindexRequired({ source: 'fields-save' });
      } else if (indexingApi && typeof indexingApi.startFull === 'function') {
        try {
          await indexingApi.startFull(shopId);
          dispatch(enqueueInfoSnackbar(getLocalizedMessage('operation.indexing.started', {})));
        } catch (indexingError) {
          dispatch(enqueueErrorSnackbar(
            indexingError?.message || getLocalizedMessage('operation.indexing.startFailed', {}),
          ));
        }
      }
      return result.fields;
    } catch (err) {
      dispatch(enqueueErrorSnackbar(getLocalizedMessage('fields.saveError', {})));
      return rejectWithValue(err?.message || 'save failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeFields(fields) {
  const entities = {};
  const ids = [];
  (fields || []).forEach((f) => {
    entities[f.id] = f;
    ids.push(f.id);
  });
  return { entities, ids };
}

function shallowEqualField(a, b) {
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const fieldsSlice = createSlice({
  name: 'fields',
  initialState,
  reducers: {
    updateField: (state, { payload }) => {
      const { id, changes } = payload;
      if (state.entities[id]) {
        const next = { ...state.entities[id], ...changes };
        if (shallowEqualField(next, state.entities[id])) {
          return;
        }
        state.entities[id] = next;
        if (shallowEqualField(next, state.savedSnapshot[id])) {
          state.modifiedIds = state.modifiedIds.filter((modifiedId) => modifiedId !== id);
        } else if (!state.modifiedIds.includes(id)) {
          state.modifiedIds.push(id);
        }
      }
    },
    discardFields: (state) => {
      state.entities = { ...state.savedSnapshot };
      state.modifiedIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFields.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadFields.fulfilled, (state, { payload }) => {
        const { entities, ids } = normalizeFields(payload);
        state.entities = entities;
        state.ids = ids;
        state.savedSnapshot = { ...entities };
        state.modifiedIds = [];
        state.isFetching = false;
        state.loadError = null;
      })
      .addCase(loadFields.rejected, (state, { payload }) => {
        state.isFetching = false;
        state.error = payload || 'load failed';
        state.loadError = payload || 'load failed';
      })
      .addCase(saveFields.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        // loadError intentionally not cleared on save — load state is unaffected
      })
      .addCase(saveFields.fulfilled, (state, { payload }) => {
        const { entities, ids } = normalizeFields(payload);
        state.entities = entities;
        state.ids = ids;
        state.savedSnapshot = { ...entities };
        state.modifiedIds = [];
        state.isSaving = false;
      })
      .addCase(saveFields.rejected, (state, { payload }) => {
        state.isSaving = false;
        state.error = payload || 'save failed';
        // loadError NOT set — save failure must not hide the page surface
      });
  },
});

export const { actions } = fieldsSlice;
export default fieldsSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectAllFields = createSelector(
  (state) => state.fields.entities,
  (state) => state.fields.ids,
  (entities, ids) => ids.map((id) => entities[id]).filter(Boolean),
);

export const selectHasChanges = (state) => state.fields.modifiedIds.length > 0;
export const selectIsFetching = (state) => state.fields.isFetching;
export const selectIsSaving = (state) => state.fields.isSaving;
export const selectError = (state) => state.fields.error;
export const selectLoadError = (state) => state.fields.loadError;
